import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/lib/utils";
import { RefreshCw, Eye, EyeOff, Plane, MapPin, BarChart3 } from "lucide-react";

export default function RouteMapSection({ game, team }) {
  const { t } = useTranslation();
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibility, setVisibility] = useState({});
  const [hoveredTeam, setHoveredTeam] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef({});

  useEffect(() => {
    if (game?.id) fetchMapData();
  }, [game?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapData && !mapInstanceRef.current) initializeMap();
  }, [mapData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapInstanceRef.current && mapData) renderLayers();
  }, [visibility, mapData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapData) return;
    Object.entries(layersRef.current).forEach(([tid, layers]) => {
      layers.forEach(layer => {
        if (layer.setOpacity) {
          layer.setOpacity(!hoveredTeam || hoveredTeam === tid ? 1 : 0.15);
        }
        if (layer.setStyle) {
          layer.setStyle({ opacity: !hoveredTeam || hoveredTeam === tid ? 0.85 : 0.12 });
        }
      });
    });
  }, [hoveredTeam, mapData]);

  const fetchMapData = async () => {
    try {
      const res = await axios.get(`${API}/games/${game.id}/route-map`);
      setMapData(res.data);
      const vis = {};
      (res.data.team_routes || []).forEach(tr => { vis[tr.team_id] = true; });
      setVisibility(vis);
    } catch (err) {
      console.error("RouteMapSection:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || !mapData) return;
    if (!window.L) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => createMap();
      document.body.appendChild(script);
    } else {
      createMap();
    }
  };

  const createMap = () => {
    if (!window.L || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: true });

    // CARTO basemaps now stamp "API KEY REQUIRED" on unauthenticated tiles.
    // Default to keyless OSM tiles; allow overriding with a paid provider via env.
    const tileUrl = process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
    const tileAttribution = process.env.REACT_APP_MAP_TILE_ATTRIBUTION
      || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

    L.tileLayer(tileUrl, {
      attribution: tileAttribution,
      maxZoom: 18,
    }).addTo(map);

    mapInstanceRef.current = map;
    fitBounds();
    renderLayers();
  };

  const fitBounds = () => {
    const map = mapInstanceRef.current;
    const L = window.L;
    if (!map || !L || !mapData) return;
    const pts = [];
    mapData.team_routes.forEach(tr => {
      if (tr.hub_coords) pts.push(tr.hub_coords);
      tr.routes.forEach(r => {
        if (r.coords?.[0]) pts.push(r.coords[0]);
        if (r.coords?.[1]) pts.push(r.coords[1]);
      });
    });
    if (pts.length > 0) {
      map.fitBounds(L.latLngBounds(pts), { padding: [25, 25], maxZoom: 6 });
    } else {
      map.setView([20, 0], 2);
    }
  };

  const renderLayers = () => {
    const map = mapInstanceRef.current;
    const L = window.L;
    if (!map || !L || !mapData) return;

    // Clear all existing layers
    Object.values(layersRef.current).forEach(group => {
      group.forEach(layer => map.removeLayer(layer));
    });
    layersRef.current = {};

    mapData.team_routes.forEach(tr => {
      if (!visibility[tr.team_id]) return;
      const layers = [];
      const color = tr.color;
      const isOwn = tr.team_id === team?.id;

      // Hub marker
      if (tr.hub_coords) {
        const hubIcon = L.divIcon({
          className: "",
          html: `<div style="
            background: ${color}; color: #fff; width: 34px; height: 34px;
            border-radius: 50%; display: flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 10px; letter-spacing: 0.5px;
            border: 3px solid rgba(255,255,255,0.9); box-shadow: 0 2px 10px rgba(0,0,0,0.5);
          ">${tr.hub_id || "HUB"}</div>`,
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        });
        const hubPopup = `
          <div style="font-family: system-ui; min-width: 180px;">
            <div style="font-weight:700; font-size:14px; color:${color}; margin-bottom:4px;">${tr.team_name}</div>
            <div style="font-size:11px; color:#94a3b8; margin-bottom:8px;">Hub: ${tr.hub_name || tr.hub_id} (${tr.airline_code})</div>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:4px; font-size:12px;">
              <span style="color:#94a3b8;">Market Share</span><span style="font-weight:600;">${tr.metrics.market_share}%</span>
              <span style="color:#94a3b8;">Brand Rating</span><span style="font-weight:600;">${tr.metrics.brand_rating}</span>
              <span style="color:#94a3b8;">Satisfaction</span><span style="font-weight:600;">${tr.metrics.customer_satisfaction}</span>
              <span style="color:#94a3b8;">Routes</span><span style="font-weight:600;">${tr.metrics.route_count}</span>
              <span style="color:#94a3b8;">Fleet</span><span style="font-weight:600;">${tr.metrics.fleet_size}</span>
            </div>
          </div>`;
        const hm = L.marker(tr.hub_coords, { icon: hubIcon }).bindPopup(hubPopup).addTo(map);
        layers.push(hm);
      }

      // Routes
      tr.routes.forEach(route => {
        if (!route.coords || route.coords.length < 2) return;

        // Curved great-circle-ish path
        const pts = generateArc(route.coords[0], route.coords[1], 30);

        const polyline = L.polyline(pts, {
          color,
          weight: isOwn ? 3 : 2,
          opacity: 0.85,
          dashArray: isOwn ? null : "6, 8",
        }).addTo(map);

        const routePopup = `
          <div style="font-family: system-ui; min-width: 160px;">
            <div style="font-weight:700; font-size:13px; color:${color};">${tr.team_name} — ${route.code}</div>
            <div style="font-size:12px; margin-top:4px;">${route.name}</div>
            <div style="font-size:11px; color:#94a3b8; margin-top:6px;">
              ${route.flights} flights/wk &middot; ${route.aircraft_count} aircraft &middot; ${route.class_config?.replace("_", " ")}
            </div>
          </div>`;
        polyline.bindPopup(routePopup);
        layers.push(polyline);

        // Destination dot
        const destIcon = L.divIcon({
          className: "",
          html: `<div style="
            background:${color}; width:16px; height:16px; border-radius:50%;
            border:2px solid rgba(255,255,255,0.8); box-shadow:0 1px 4px rgba(0,0,0,0.3);
            display:flex; align-items:center; justify-content:center;
            font-size:7px; color:#fff; font-weight:700;
          ">${route.code.replace("R0","").replace("R","")}</div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        const dm = L.marker(route.coords[1], { icon: destIcon })
          .bindPopup(`<strong>${route.dest_city || route.name}</strong><br/>${tr.team_name} — ${route.code}`)
          .addTo(map);
        layers.push(dm);
      });

      layersRef.current[tr.team_id] = layers;
    });
  };

  const toggleVisibility = (teamId) => {
    setVisibility(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="h-96 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="text-slate-500 ml-3">{t("routeMap.loadingMap")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!mapData?.team_routes?.length) {
    return (
      <Card>
        <CardContent className="h-48 flex items-center justify-center">
          <p className="text-slate-400">{t("routeMap.noData")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="route-map-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold text-slate-900">{t("nav.routeMap")}</h3>
          <p className="text-sm text-slate-500">{t("routeMap.competitivePanel")}</p>
        </div>
        <button
          onClick={fetchMapData}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200 hover:bg-slate-100 transition-colors"
          data-testid="route-map-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t("common.refresh")}
        </button>
      </div>

      {/* Map + Legend side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <Card className="lg:col-span-3 overflow-hidden">
          <CardContent className="p-0">
            <div
              ref={mapRef}
              className="h-[520px]"
              data-testid="route-map-canvas"
            />
          </CardContent>
        </Card>

        {/* Interactive Legend / Control Panel */}
        <Card className="lg:col-span-1" data-testid="route-map-legend">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-slate-500" />
              {t("routeMap.controlPanel")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-1.5 max-h-[470px] overflow-y-auto">
            {mapData.team_routes.map(tr => {
              const isOwn = tr.team_id === team?.id;
              const visible = visibility[tr.team_id] !== false;
              const isHovered = hoveredTeam === tr.team_id;
              return (
                <div
                  key={tr.team_id}
                  className={`rounded-lg border p-2.5 transition-all duration-200 cursor-pointer select-none ${
                    isHovered
                      ? "ring-2 ring-offset-1 shadow-md"
                      : isOwn
                        ? "bg-sky-950/5 border-sky-200"
                        : "bg-slate-50 border-slate-200 hover:border-slate-300"
                  } ${!visible ? "opacity-50" : ""}`}
                  style={isHovered ? { borderColor: tr.color, ringColor: tr.color } : {}}
                  onMouseEnter={() => setHoveredTeam(tr.team_id)}
                  onMouseLeave={() => setHoveredTeam(null)}
                  data-testid={`legend-team-${tr.team_id}`}
                >
                  {/* Team header row */}
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3.5 h-3.5 rounded-full shrink-0 border border-white/50"
                      style={{ backgroundColor: tr.color, boxShadow: `0 0 6px ${tr.color}40` }}
                    />
                    <span className="font-semibold text-xs text-slate-800 truncate flex-1">
                      {tr.team_name}
                    </span>
                    <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0">{tr.airline_code}</Badge>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleVisibility(tr.team_id); }}
                      className="p-0.5 rounded hover:bg-slate-200 transition-colors"
                      data-testid={`legend-toggle-${tr.team_id}`}
                      title={visible ? t("routeMap.hide") : t("routeMap.show")}
                    >
                      {visible
                        ? <Eye className="w-3.5 h-3.5 text-slate-500" />
                        : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                  </div>

                  {/* Hub label */}
                  {tr.hub_name && (
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] text-slate-500">{tr.hub_name}</span>
                    </div>
                  )}

                  {/* Competitive metrics */}
                  <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 mt-1.5 text-[10px]">
                    <MetricCell label="Mkt" value={`${tr.metrics.market_share}%`} />
                    <MetricCell label="Brand" value={tr.metrics.brand_rating} />
                    <MetricCell label="Sat" value={tr.metrics.customer_satisfaction} />
                  </div>

                  {/* Route count chip */}
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                    <Plane className="w-3 h-3" />
                    <span>{tr.routes.length} {t("routeMap.routesLabel")} &middot; {tr.metrics.fleet_size} {t("routeMap.fleetLabel")}</span>
                    {tr.is_ai && <Badge variant="secondary" className="text-[8px] h-3.5 px-1">AI</Badge>}
                    {isOwn && <Badge className="text-[8px] h-3.5 px-1 bg-sky-600">{t("routeMap.you")}</Badge>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-slate-400 leading-none">{label}</span>
      <span className="font-semibold text-slate-700 leading-tight">{value}</span>
    </div>
  );
}

/** Simple geodesic arc approximation between two [lat,lng] points */
function generateArc(start, end, segments = 30) {
  const toRad = d => (d * Math.PI) / 180;
  const toDeg = r => (r * 180) / Math.PI;
  const [lat1, lng1] = [toRad(start[0]), toRad(start[1])];
  const [lat2, lng2] = [toRad(end[0]), toRad(end[1])];
  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2
    )
  );
  if (d < 1e-10) return [start, end];
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const f = i / segments;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
    const y = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    pts.push([toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))]);
  }
  return pts;
}
